"use client"
import ReactMarkdown from 'react-markdown';
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAssistantContext } from "../../contexts/AssistantContext";
import { useAuth } from "../../useAuth";
// eslint-disable-next-line no-unused-vars
import { useOrganization } from "../../contexts/OrganizationContext";
import useToast from "../../hooks/useToast";
import {
  ArrowLeft,
  Plus,
  LayoutGrid,
  List as LayoutList,
  MoreHorizontal,
  Send,
  X,
  Loader
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Slider } from "../ui/slider";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../ui/select";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../ui/dropdown-menu";
import ToastContainer from "../ui/ToastContainer";
import ConfirmDialog from "../ui/ConfirmDialog";
import assistantsBot from "../../assets/assistants-bot.png";
import { useZeno } from "../../hooks/useZeno";
import AssistantService from "../../services/assistant.service";
import ChatService from "../../services/chat.service";
import AssistantConfigureView from "./AssistantConfigureView";

const AssistantCard = React.memo(React.forwardRef(({
  assistant,
  index,
  isHovered,
  hoveredAssistantId,
  onMouseEnter,
  onMouseLeave,
  onClick,
  onChatClick,
  onDelete,
  assistantsBot
}, ref) => (
  <div
    ref={ref}
    className="animate-slide-blur [animation-fill-mode:forwards]"
    style={{ animationDelay: `${index * 0.06}s` }}
  >
    <div
      className={`rounded-3xl overflow-hidden p-0 w-[220px] bg-white/10 backdrop-blur-sm border border-white/20 transition-all duration-300 ease-out group cursor-pointer transform-gpu ${
        hoveredAssistantId !== null
          ? isHovered
            ? 'ring-1 ring-purple-400/30 scale-[1.02] z-50 shadow-xl shadow-purple-500/10 bg-white/[0.12] border-purple-400/30'
            : 'opacity-50 scale-[0.98]'
          : 'hover:border-white/30 hover:bg-white/[0.12]'
      }`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      <div className="pt-4 pb-3 flex items-center justify-center">
        <div className="w-[140px] h-[140px] rounded-lg bg-white shadow-sm ring-1 ring-white/10 overflow-hidden">
          <img
            src={assistant.avatar || assistantsBot}
            alt={assistant.name}
            className="w-full h-full object-cover rounded-lg"
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>

      <div className="px-4 pb-4 flex flex-col h-[140px]">
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-white mb-2 line-clamp-1">{assistant.name}</h3>
          <p className="text-xs text-gray-400 leading-4 line-clamp-2 mb-3">
            {assistant.description || 'No description'}
          </p>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {assistant.isDraft && (
              <span className="text-xs px-2 py-1 rounded-full bg-yellow-900/30 text-yellow-400 flex-shrink-0">
                Draft
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onChatClick(assistant);
              }}
              className="bg-purple-600 hover:bg-purple-700 text-white w-8 h-8 rounded-lg transition-all duration-300 flex items-center justify-center hover:scale-110 hover:shadow-lg hover:shadow-purple-500/30 transform-gpu"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="text-gray-400 hover:text-white hover:bg-white/10 rounded-lg w-8 h-8 flex items-center justify-center transition-colors duration-200"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-black/90 border border-gray-800 rounded-xl shadow-lg"
              >
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(assistant.id);
                  }}
                  className="text-red-400 hover:bg-red-900/60 hover:text-red-300"
                >
                  Delete
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onChatClick(assistant);
                  }}
                  className="text-slate-300 hover:bg-black/60 hover:text-white"
                >
                  Chat
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </div>
  </div>
)));

AssistantCard.displayName = 'AssistantCard';

export default function AssistantsView({ projectId }) {
  // Context y hooks
  // Estado para el hover de assistant en grid
  const [hoveredAssistantId, setHoveredAssistantId] = useState(null);
  // Estado para controlar el projectId actual y evitar renders con datos del proyecto anterior
  const [currentProjectId, setCurrentProjectId] = useState(projectId);
  const {
    assistants,
    loading: assistantsLoading,
    // eslint-disable-next-line no-unused-vars
    error: assistantsError,
    // eslint-disable-next-line no-unused-vars
    selectedAssistant,
    // eslint-disable-next-line no-unused-vars
    setSelectedAssistant,
    loadAssistants,
    createAssistant,
    updateAssistant,
    deleteAssistant,
    getAssistant,
    templates: contextTemplates,
    llmModels,
  } = useAssistantContext();

  // Estado local para templates/avatars refrescables
  const [templates, setTemplates] = useState(contextTemplates || []);
  
  // Estado para skills disponibles desde la API
  const [availableSkills, setAvailableSkills] = useState([]);
  
  const { user } = useAuth();
  const { currentAccount } = useOrganization();
  const accountId = currentAccount?.id;
  // eslint-disable-next-line no-unused-vars
  const userAvatar = user?.avatar_url || "/placeholder-user.png";
  
  // Toast notifications
  // eslint-disable-next-line no-unused-vars
  const { toasts, removeToast, success, error, warning, info } = useToast();
  
  // Hook de Zeno para el tab de chat
  const { 
    // eslint-disable-next-line no-unused-vars
    messages: zenoMessages, 
    // eslint-disable-next-line no-unused-vars
    sending: zenoSending, 
    chatWithZeno 
  } = useZeno();

    // --- Sidebar historial de chats ---
    const [showSidebar, setShowSidebar] = useState(false);
    const [chatHistories, setChatHistories] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [chatHistoryPage, setChatHistoryPage] = useState(0); // For pagination
    const CHAT_HISTORY_PAGE_SIZE = 5;
    // eslint-disable-next-line no-unused-vars
    const [loadingHistories, setLoadingHistories] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [selectedHistory, setSelectedHistory] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [historyMessages, setHistoryMessages] = useState([]);
    const [hasScrollableHistories, setHasScrollableHistories] = useState(false);
    const totalPages = Math.max(1, Math.ceil(chatHistories.length / CHAT_HISTORY_PAGE_SIZE));

    // Abrir sidebar y cargar historiales
    const fetchAllChatHistories = async () => {
      if (!accountId && !user?.user_id) return;
      if (!chattingAssistant?.id) return;
      
      setLoadingHistories(true);
      try {
        // Usar el nuevo método que filtra por assistant específico
        const result = await AssistantService.listChatHistoriesByAssistant({
          accountId,
          assistantId: chattingAssistant.id,
          limit: 15,
          offset: 0,
          sort_by: 'updated_at',
          descending: true
        });
        if (result.success && Array.isArray(result.data)) {
          setChatHistories(result.data);
        } else {
          setChatHistories([]);
        }
      } catch (err) {
        setChatHistories([]);
        console.error('Error fetching chat histories for assistant:', err);
      } finally {
        setLoadingHistories(false);
      }
    };

    const openSidebar = async () => {
      setShowSidebar(true);
      setChatHistoryPage(0);
      await fetchAllChatHistories();
    };

    // Detectar si hay contenido scrolleable en el historial
    useEffect(() => {
      if (!chatHistories.length) {
        setHasScrollableHistories(false);
        return;
      }

      const checkScrollable = () => {
        const scrollContainer = document.getElementById("chat-history-scroll-chat");
        if (scrollContainer) {
          const hasScroll = scrollContainer.scrollHeight > scrollContainer.clientHeight;
          setHasScrollableHistories(hasScroll);
        }
      };

      // Verificar inmediatamente y después de un delay para asegurar el render
      checkScrollable();
      const timeoutId = setTimeout(checkScrollable, 50);
      
      return () => clearTimeout(timeoutId);
    }, [chatHistories.length]); // Solo depender del length, no del array completo

    // eslint-disable-next-line no-unused-vars
    const handleChatHistoryPageChange = (newPage) => {
      if (newPage >= 0 && newPage < totalPages) {
        setChatHistoryPage(newPage);
      }
    };

    // Cerrar sidebar
    // eslint-disable-next-line no-unused-vars
    const closeSidebar = () => {
      setShowSidebar(false);
      setSelectedHistory(null);
      setHistoryMessages([]);
    };

    // Seleccionar historial y cargar mensajes
    const handleSelectHistory = async (history) => {
      setLoadingHistories(true);
      try {
        // Solo cargar los mensajes del historial seleccionado, filtrando los de role 'system'
        const messagesResult = await ChatService.getChatMessages(history.id);
        if (messagesResult.success && Array.isArray(messagesResult.data)) {
          const filteredMessages = messagesResult.data.filter(msg => msg.role !== 'system');
          setChatMessages(filteredMessages);
          setChatHistoryId(history.id);
        } else {
          setChatMessages([]);
        }
      } catch (err) {
        setChatMessages([]);
      } finally {
        setLoadingHistories(false);
      }
    };

    // Volver al listado de historiales
    // eslint-disable-next-line no-unused-vars
    const handleBackToList = () => {
      setSelectedHistory(null);
      setHistoryMessages([]);
    };
  const [viewMode, setViewMode] = useState("grid")
  const [creating, setCreating] = useState(false)
  // Refrescar templates y skills al abrir el modal de crear assistant
  const handleOpenCreate = async () => {
    try {
      // Fetch templates
      const result = await AssistantService.listTemplates();
      if (result.success && Array.isArray(result.data)) {
        setTemplates(result.data);
      } else {
        setTemplates(contextTemplates || []);
      }
      
      // Fetch skills
      const skillsResult = await AssistantService.getSkills();
      if (skillsResult.success && skillsResult.data?.skills && Array.isArray(skillsResult.data.skills)) {
        setAvailableSkills(skillsResult.data.skills);
      } else {
        setAvailableSkills([]);
      }
    } catch (e) {
      setTemplates(contextTemplates || []);
      setAvailableSkills([]);
    }
    resetForm();
    setCreating(true);
  };
  const [editing, setEditing] = useState(false) // Nuevo estado para edición
  const [editingAssistant, setEditingAssistant] = useState(null) // Assistant siendo editado
  const [tab, setTab] = useState("configure")
  const [submitting, setSubmitting] = useState(false)
  
  // Confirmation dialog state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [assistantToDelete, setAssistantToDelete] = useState(null)

  // form state
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [selectedModel, setSelectedModel] = useState("b7cad7e6-7369-4a1a-a3e1-1923615d6ccc") // GPT-4o Mini por defecto
  const [selectedTemplate, setSelectedTemplate] = useState("00000000-0000-0000-0000-000000000001") // General por defecto
  const [language, setLanguage] = useState("es")
  const [selectedSkills, setSelectedSkills] = useState("none") // Sin skill seleccionado por defecto
  const [responseFormat, setResponseFormat] = useState("text")
  const [temperature, setTemperature] = useState(100) // 1.0 por defecto
  const [maxTokens, setMaxTokens] = useState(2048)
  const [memoryEnabled, setMemoryEnabled] = useState(true)
  const [isDraft, setIsDraft] = useState(false)
  const [outputMode, setOutputMode] = useState("normal")
  const [jsonSchema, setJsonSchema] = useState("")
  const [strictValidation, setStrictValidation] = useState(true)
  const [prettyPrint, setPrettyPrint] = useState(false)
  const [indent, setIndent] = useState(2)
  const [avatarBase64, setAvatarBase64] = useState("")
  const [avatarUrl, setAvatarUrl] = useState("")

  // Sections
  const [sections, setSections] = useState([]); // {id,title,html,markdown}

  // Chat UI states
  const [chattingAssistant, setChattingAssistant] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const [chatHistoryId, setChatHistoryId] = useState(null);

  // Auto-scroll del chat cuando se agregan nuevos mensajes
  useEffect(() => {
    if (chatMessages.length > 0) {
      setTimeout(() => {
        const scrollContainer = document.getElementById('chat-message-scroll-area');
        if (scrollContainer) {
          scrollContainer.scrollTo({ 
            top: scrollContainer.scrollHeight, 
            behavior: 'smooth' 
          });
        }
      }, 100);
    }
  }, [chatMessages]);

  // Agregar estilos de scrollbar personalizados
  useEffect(() => {
    const scrollbarStyles = `
      .message-bubble-user::-webkit-scrollbar {
        width: 4px;
      }
      .message-bubble-user::-webkit-scrollbar-track {
        background: transparent;
      }
      .message-bubble-user::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.3);
        border-radius: 10px;
        border: none;
      }
      .message-bubble-user::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.5);
      }
      .message-bubble-user::-webkit-scrollbar-corner {
        background: transparent;
      }
      
      .message-bubble-assistant::-webkit-scrollbar {
        width: 4px;
      }
      .message-bubble-assistant::-webkit-scrollbar-track {
        background: transparent;
      }
      .message-bubble-assistant::-webkit-scrollbar-thumb {
        background: rgba(156, 163, 175, 0.4);
        border-radius: 10px;
        border: none;
      }
      .message-bubble-assistant::-webkit-scrollbar-thumb:hover {
        background: rgba(156, 163, 175, 0.6);
      }
      .message-bubble-assistant::-webkit-scrollbar-corner {
        background: transparent;
      }
      
      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: translateY(-5px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      .animate-fade-in {
        animation: fadeIn 0.3s ease-out;
      }
    `;

    // Agregar estilos al head si no existen
    if (!document.getElementById('message-scrollbar-styles')) {
      const style = document.createElement('style');
      style.id = 'message-scrollbar-styles';
      style.textContent = scrollbarStyles;
      document.head.appendChild(style);
    }
  }, []);

  // Función para prevenir el scroll del contenedor principal cuando el mensaje llega a sus límites
  const handleMessageScroll = (e) => {
    const element = e.target;
    const { scrollTop, scrollHeight, clientHeight } = element;
    const deltaY = e.deltaY;

    // Si está intentando hacer scroll hacia abajo y ya está en el fondo
    if (deltaY > 0 && scrollTop + clientHeight >= scrollHeight) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // Si está intentando hacer scroll hacia arriba y ya está en la parte superior
    if (deltaY < 0 && scrollTop <= 0) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
  };

  // Función para prevenir scroll de la página cuando mouse está sobre contenedor del chat
  const handleChatContainerScroll = (e) => {
    const target = e.target;
    const currentElement = e.currentTarget;
    
    // Si el target es el propio contenedor (no un elemento scrolleable interno), prevenir
    if (target === currentElement || target.scrollHeight <= target.clientHeight) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // Cargar assistants filtrados por proyecto
  useEffect(() => {
    if (projectId && projectId !== currentProjectId) {
      // Actualizar el projectId actual inmediatamente
      setCurrentProjectId(projectId);
      
      // Limpiar estados locales al cambiar de proyecto
      setChattingAssistant(null);
      setChatMessages([]);
      setChatHistoryId(null);
      setEditing(false);
      setEditingAssistant(null);
      setCreating(false);
      setHoveredAssistantId(null); // Reset hover state
      
      // Cargar assistants del nuevo proyecto
      loadAssistants({ projectId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Reset hover state when assistants change to prevent sticky hover
  useEffect(() => {
    setHoveredAssistantId(null);
  }, [assistants]);

  // Filtrar asistentes por el proyecto actual para evitar mostrar asistentes de otros proyectos
  const filteredAssistants = useMemo(
    () => assistants.filter(assistant => assistant.projectId === currentProjectId),
    [assistants, currentProjectId]
  );

  /**
   * Función para limpiar assistants que ya no existen en el servidor
   */
  const cleanupDeletedAssistants = async () => {
    if (!assistants.length) return;

    let hasChanges = false;
    
    for (const assistant of assistants) {
      try {
        const result = await getAssistant(assistant.id);
        if (!result.success) {
          hasChanges = true;
        }
      } catch (error) {
        hasChanges = true;
      }
    }

    if (hasChanges) {
      await loadAssistants({ projectId });
    }
  };

  /**
   * Ejecutar limpieza cuando cambien los assistants
   */
  useEffect(() => {
    if (assistants.length > 0) {
      cleanupDeletedAssistants();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo ejecutar una vez cuando se cargan los assistants

  /**
   * Manejar creación de assistant con el servicio real
   */
  const handleCreate = async () => {
    if (!name.trim()) {
      warning('Required Field', 'Please enter a name for your assistant');
      return;
    }

    setSubmitting(true);

    try {
      // Obtener el modelo seleccionado
      const selectedLLM = llmModels.find(m => m.id === selectedModel) || llmModels[0];
      
      // Crear assistant usando la estructura exacta de la documentación API
      const assistantData = {
        name: name.trim(),
        description: description.trim() || `AI Assistant created on ${new Date().toLocaleDateString()}`,
        type: "general", // Required: "workflow" | "config" | "general" | "custom"
        skills: selectedSkills && selectedSkills !== "none" ? [selectedSkills] : [], // Solo agregar skill si hay uno seleccionado
        avatar: (avatarBase64 && avatarBase64.trim() !== '') ? avatarBase64 : (avatarUrl && avatarUrl.trim() !== '') ? avatarUrl : null,
        reactAgentId: null,
        memory: "",
        executionMode: "reactive",
        projectId: projectId,
        config: {
          llm_id: selectedModel,
          model_label: selectedLLM?.name || "GPT-4o Mini",
          assistant_template: {
            id: selectedTemplate,
            name: templates.find(t => t.id === selectedTemplate)?.name || "General Assistant",
            description: "A versatile general-purpose assistant template",
            is_default: selectedTemplate === "00000000-0000-0000-0000-000000000001",
            sections: sections.map((section, index) => ({
              id: `section-${index}`,
              template_id: selectedTemplate,
              name: section.name || `Section ${index + 1}`,
              order: index + 1,
              content: section.content || ""
            }))
          },
          template_id: selectedTemplate,
          skills: selectedSkills && selectedSkills !== "none" ? [selectedSkills] : [], // Solo incluir skills si hay selección
          temperature: temperature / 100, // Convertir de 0-100 a 0-1
          max_tokens: maxTokens,
          root_prompt: "", // Se puede usar para prompts personalizados
          reasoning_effort: "", // Campo opcional para configuración de razonamiento
          memory_enabled: memoryEnabled,
          language: language,
          is_active: true,
          isDraft: isDraft,
          outputFormat: outputMode === 'normal' ? null : {
            mode: outputMode,
            ...(outputMode === 'structured_json' || outputMode === 'stream' ? {
              json_schema: jsonSchema || null,
              strict_validation: strictValidation,
              pretty_print: prettyPrint,
              indent: indent
            } : {})
          },
          custom_parameters: {
            response_format: responseFormat
          }
        }
      };

      const result = await createAssistant(assistantData);

      if (result.success) {
        success('Created', `${name} created successfully`);
        // Resetear formulario
        resetForm();
        setCreating(false);
      } else {
        error('Creation Failed', result.error.message || 'Failed to create assistant');
      }
    } catch (error) {
      error('Unexpected Error', 'An unexpected error occurred while creating the assistant');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Manejar actualización de assistant
   */
  const handleUpdate = async (assistantId, updatedData) => {
    // Validar que el assistant existe antes de intentar actualizarlo
    if (!assistantId) {
      error('Assistant Inválido', 'Falta el ID del assistant');
      return;
    }

    setSubmitting(true);

    try {
      console.log('🔍 Verificando que el assistant existe antes de actualizar...');
      
      // Verificar que el assistant aún existe
      const existsResult = await getAssistant(assistantId);
      
      if (!existsResult.success) {
        error('Assistant No Encontrado', 'Este assistant puede haber sido eliminado. Actualizando la lista...');
        
        // Recargar la lista para sincronizar con el servidor
        await loadAssistants({ projectId });
        
        setEditing(false);
        setEditingAssistant(null);
        return;
      }

      // Agregar una segunda verificación justo antes del update para detectar problemas de timing
      const secondCheck = await getAssistant(assistantId);
      
      if (!secondCheck.success) {
        error('Assistant Eliminado', 'El assistant fue eliminado durante la edición. Actualizando la lista...');
        await loadAssistants({ projectId });
        setEditing(false);
        setEditingAssistant(null);
        return;
      }
      
      const result = await updateAssistant(assistantId, updatedData);

      if (result.success) {
        success('Actualizado', 'Assistant actualizado correctamente');
        
        // Actualizar el assistant en el estado local
        setEditingAssistant(result.data);
        
        // Recargar la lista de assistants
        await loadAssistants();
        
        // Volver a la vista principal
        setEditing(false);
        setEditingAssistant(null);
      } else {
        console.error('❌ Error en la actualización:', result.error);
        
        // Log detailed error information for 422 validation errors
        if (result.error?.status === 422) {
          
          // Si hay errores específicos de validación, mostrarlos
          if (result.error?.message && Array.isArray(result.error.message)) {
            const validationErrors = result.error.message.map(err => err.message || err).join(', ');
            error('Errores de Validación', `Los siguientes campos requieren atención: ${validationErrors}`);
          } else if (result.error?.errors) {
            const errorMessages = Object.values(result.error.errors).flat().join(', ');
            error('Errores de Validación', errorMessages);
          } else {
            error('Error de Validación', 'Los datos del assistant contienen errores de validación. Revisa todos los campos requeridos.');
          }
        }
        // Manejar errores específicos
        else if (result.error.status === 404) {
          error('Assistant No Encontrado', 'Este assistant ya no existe en el servidor. Actualizando la lista...');
          
          // Recargar la lista para sincronizar con el servidor
          await loadAssistants({ projectId });
          
          setEditing(false);
          setEditingAssistant(null);
        } else {
          error('Error de Actualización', result.error.message || 'No se pudo actualizar el assistant');
        }
      }
    } catch (error) {
      console.error('💥 Error inesperado durante la actualización:', error);
      error('Error Inesperado', 'Ocurrió un error inesperado al actualizar el assistant');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Manejar eliminación de assistant
   */
  const handleDelete = useCallback((assistantId) => {
    setAssistantToDelete(assistantId);
    setShowDeleteConfirm(true);
  }, []);

  const confirmDelete = async () => {
    if (!assistantToDelete) return;

    const result = await deleteAssistant(assistantToDelete, 'User deleted assistant');
    
    if (result.success) {
      success('Deleted', 'Assistant deleted successfully');
    } else {
      console.error('❌ Error eliminando assistant:', result.error);
      error('Deletion Failed', result.error.message || 'Failed to delete assistant');
    }
    
    setShowDeleteConfirm(false);
    setAssistantToDelete(null);
  };

  const cancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setAssistantToDelete(null);
  }, []);

  /**
   * Manejar click en assistant para obtener detalles
   * Usa GET /assistants/{assistant_id}
   */
  const handleSelectAssistant = async (assistantId) => {
    // Limpiar hover state al seleccionar assistant
    setHoveredAssistantId(null);
    
    try {
      
      const result = await getAssistant(assistantId);
      
      if (result.success) {
        
        // Cargar datos del assistant en los campos de edición
        const assistant = result.data;
        setName(assistant.name || "");
        setDescription(assistant.description || "");
        setSelectedModel(assistant.config?.llm_id || "b7cad7e6-7369-4a1a-a3e1-1923615d6ccc");
        setSelectedTemplate(assistant.config?.template_id || "00000000-0000-0000-0000-000000000001");
        setLanguage(assistant.config?.language || "es");
  const skillsRoot = Array.isArray(assistant.skills) ? assistant.skills : [];
  const skillsConfig = Array.isArray(assistant.config?.skills) ? assistant.config.skills : [];
  const allSkills = skillsRoot.length > 0 ? skillsRoot : skillsConfig;
  setSelectedSkills(allSkills);
        setResponseFormat(assistant.config?.response_format || "text");
        setTemperature(Math.round((assistant.config?.temperature || 1) * 100));
        setMaxTokens(assistant.config?.max_tokens || 2048);
        setMemoryEnabled(assistant.config?.memory_enabled ?? true);
        setSections(assistant.config?.assistant_template?.sections || []);
        
        // Cambiar a modo edición
        setEditingAssistant(assistant);
        setEditing(true);
        success('Loaded', `${assistant.name} ready for editing`);
      } else {
        console.error('❌ Error obteniendo assistant:', result.error);
        error('Load Failed', result.error.message || 'Failed to load assistant');
      }
    } catch (error) {
      console.error('Error inesperado:', error);
      error('Unexpected Error', 'An unexpected error occurred while loading the assistant');
    }
  };

  /**
   * Manejar envío de mensaje a Zeno
   */
  // eslint-disable-next-line no-unused-vars
  const handleSendToZeno = async (message) => {
    await chatWithZeno(message);
  };

  /**
   * Resetear formulario a valores por defecto
   */
  const resetForm = () => {
    setName("");
    setDescription("");
    setSelectedModel("b7cad7e6-7369-4a1a-a3e1-1923615d6ccc");
    setSelectedTemplate("00000000-0000-0000-0000-000000000001");
    setLanguage("es");
    setSelectedSkills("none");
    setResponseFormat("text");
    setTemperature(100);
    setMaxTokens(2048);
    setMemoryEnabled(true);
    setIsDraft(false);
    setOutputMode("normal");
    setJsonSchema("");
    setStrictValidation(true);
    setPrettyPrint(false);
    setIndent(2);
    setSections([]);
    setAvatarBase64("");
    setAvatarUrl("");
  };

  /**
   * Volver a la lista desde la vista de edición
   */
  const handleBackFromEditing = () => {
    setEditing(false);
    setEditingAssistant(null);
    // Limpiar hover state al salir del configure
    setHoveredAssistantId(null);
    // Reset form usando la función común
    resetForm();
  };

  // Abrir chat con assistant (sin crear chat history automáticamente)
  const handleOpenChat = useCallback(async (assistant) => {
    setChattingAssistant(assistant);
    setChatMessages([]);
    setChatInput("");
    setChatHistoryId(null); // No crear chat history hasta que se envíe el primer mensaje

    // Scroll para centrar la interfaz de chat en pantalla
    setTimeout(() => {
      const chatPanel = document.querySelector('.bg-\\[\\#18181b\\].border-white\\/20.text-white.w-full.max-w-7xl.mx-auto.rounded-2xl.shadow-2xl.flex.flex-row.overflow-hidden.mb-8.h-\\[800px\\]');
      if (chatPanel && typeof chatPanel.scrollIntoView === 'function') {
        chatPanel.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 50);
  }, []);

  // Enviar mensaje al assistant (método original simplificado)
  const handleSendMessage = async () => {
    if (!chatInput.trim() || !chattingAssistant) return;
    
    setChatSending(true);
    const messageText = chatInput;
    
    // Agregar mensaje del usuario a la UI inmediatamente
    setChatMessages((msgs) => [...msgs, { role: "user", content: messageText }]);
    setChatInput("");
    
    try {
      // Si no hay chatHistoryId, crear uno primero
      let currentChatHistoryId = chatHistoryId;
      let isNewChatHistory = false; // Flag para saber si es el primer mensaje
      
      if (!currentChatHistoryId) {
        isNewChatHistory = true;
        const chatResult = await ChatService.createChatHistory({
          accountId,
          userId: user?.user_id,
          assistantId: chattingAssistant.id,
          userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          initialMessage: messageText
        });
        
        if (chatResult.success) {
          currentChatHistoryId = chatResult.data.id;
          setChatHistoryId(currentChatHistoryId);
        } else {
          error("Chat Error", chatResult.error?.message || "Error creating chat history");
          setChatMessages((msgs) => msgs.filter(m => !(m.role === "user" && m.content === messageText)));
          return;
        }
      }
      
      // Usar el método original de AssistantService
      const payload = {
        assistant_id: chattingAssistant.id,
        chat_history_id: currentChatHistoryId,
        userId: user?.user_id,
        accountId,
        content_blocks: [
          {
            type: "text",
            data: { text: messageText },
            order: 1
          }
        ]
      };
      
      const result = await AssistantService.sendMessageToAssistant(payload);
      
      if (result.success) {
        // Método original: agregar solo la respuesta del assistant
        setChatMessages((msgs) => [...msgs, { role: "assistant", ...result.data }]);
        
        // Solo actualizar la lista de chat histories si es el primer mensaje (nuevo chat)
        // Usar un delay para dar tiempo al backend de generar el título
        if (isNewChatHistory) {
          setTimeout(async () => {
            await fetchAllChatHistories();
          }, 1500);
        }
      } else {
        error("Chat Error", result.error?.message || "Error sending message");
        // NO remover el mensaje del usuario si ya se creó el chat history
        // Solo remover si no se había creado el chat history antes
        if (!chatHistoryId) {
          setChatMessages((msgs) => msgs.filter(m => !(m.role === "user" && m.content === messageText)));
        }
      }
    } catch (err) {
      error("Chat Error", "Unexpected error");
      console.error('Error sending message:', err);
      // NO remover el mensaje del usuario si ya se creó el chat history
      // Solo remover si no se había creado el chat history antes  
      if (!chatHistoryId) {
        setChatMessages((msgs) => msgs.filter(m => !(m.role === "user" && m.content === messageText)));
      }
    } finally {
      setChatSending(false);
    }
  };

  // Cerrar chat
  const handleCloseChat = () => {
    setChattingAssistant(null);
    setChatMessages([]);
    setChatInput("");
    setChatSending(false);
  };

  // Cargar chat histories al abrir el chat con assistant
  useEffect(() => {
    if (chattingAssistant && accountId) {
      // Cargar histories específicos de este assistant
      AssistantService.listChatHistoriesByAssistant({
        accountId,
        assistantId: chattingAssistant.id,
        limit: 15,
        offset: 0,
        sort_by: 'updated_at',
        descending: true
      }).then(result => {
        if (result.success && Array.isArray(result.data)) {
          setChatHistories(result.data);
          console.log(`📋 Loaded ${result.data.length} chat histories for ${chattingAssistant.name}`);
        } else {
          setChatHistories([]);
          console.log('No chat histories found for this assistant');
        }
      }).catch(err => {
        console.error('Error loading initial chat histories for assistant:', err);
        setChatHistories([]);
      });
    }
  }, [chattingAssistant, accountId]);

  // ------------------ PANTALLA “NEW ASSISTANT” ------------------
  if (creating) {
    return (
      <div className="space-y-6 p-8 min-h-screen">
        {/* Header */}
        <div className="relative">
          <div>
            <div className="flex items-center justify-between h-[57px] px-8 bg-black w-full">
              <div className="flex items-center gap-2">
                <button
                  className="flex items-center justify-center w-9 h-9 text-white hover:bg-white/10 transition"
                  onClick={() => {
                    resetForm();
                    setCreating(false);
                  }}
                  aria-label="Back"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <span className="text-lg font-light text-white ml-1">New Assistant</span>
              </div>
              <button
                onClick={handleCreate}
                disabled={submitting || !name.trim()}
                className="font-light text-white text-[15px] flex items-center justify-center bg-[#7c3aed] hover:bg-[#6B3FC1] disabled:bg-gray-600 disabled:cursor-not-allowed rounded-[10px] w-[123px] h-[29px] p-0 min-w-0"
              >
                {submitting ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create'
                )}
              </button>
            </div>
            <div className="border-b border-[#23242A]" />
          </div>
        </div>

        {/* Tabs pill */}
        <div className="flex bg-[#2C2C36] rounded-full p-1 w-fit mb-6">
          <button
            onClick={() => setTab("configure")}
            className={`px-6 py-2 rounded-full text-sm font-medium transition
              ${tab === "configure" ? "bg-[#58585F] text-white" : "text-gray-400 hover:bg-white/10 hover:text-white"}`}
          >
            Configure
          </button>
        </div>

        <div className="h-[calc(100vh-200px)]">
          {/* Panel principal (sin panel derecho) */}
          <div className="bg-black/70 rounded-2xl p-6 flex flex-col border border-white/20 relative">
            {/* Form Configure */}
                <div className="flex-1 overflow-y-auto pr-2">
                  <div className="space-y-6">
                    {/* AvatarUploader + Name */}
                    <div className="flex items-start gap-4">
                      <div className="relative w-32 h-32 rounded-xl border border-white/20 bg-white/10 overflow-hidden group">
                        {(avatarBase64 || avatarUrl) ? (
                          <img
                            src={avatarBase64 || avatarUrl}
                            alt="Avatar"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center text-center">
                            <Loader className="w-8 h-8 text-gray-400 mb-2" />
                            <span className="text-gray-400 text-xs">No Avatar</span>
                          </div>
                        )}
                        {/* Overlay con botón Change al hacer hover */}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="bg-white/20 hover:bg-white/30 text-white border border-white/30 backdrop-blur-sm"
                            onClick={() => document.getElementById('avatar-upload-create').click()}
                          >
                            <Loader className="w-4 h-4 mr-2" />
                            Change
                          </Button>
                        </div>
                        {/* Hidden file input */}
                        <input
                          id="avatar-upload-create"
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onload = (event) => {
                                const base64 = event.target.result;
                                setAvatarBase64(base64);
                                setAvatarUrl("");
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          className="hidden"
                        />
                      </div>
                      <div className="flex-1 max-w-xs">
                        <label className="text-gray-300 text-xs font-medium mb-1 block">Paste image URL:</label>
                        <Input
                          value={avatarUrl}
                          onChange={e => {
                            setAvatarUrl(e.target.value);
                            if (e.target.value) setAvatarBase64("");
                          }}
                          placeholder="https://example.com/avatar.png"
                          className="w-full bg-black/60 border border-white/20 text-white placeholder-gray-500"
                        />
                        <label className="block text-white mb-1 mt-4">Name</label>
                        <Input
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Assistant name"
                          className="w-full bg-black/60 border border-white/40 text-white placeholder-gray-500 rounded-lg px-4 py-2"
                        />
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-white mb-1">Description</label>
                      <Input
                        as="textarea"
                        rows={3}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Describe your assistant"
                        className="w-full bg-black/60 border border-white/40 text-white placeholder-gray-500 rounded-lg px-4 py-2"
                      />
                    </div>

                    {/* LLM Model Selection */}
                    <div>
                      <label className="block text-white mb-2">LLM Model</label>
                      <Select value={selectedModel} onValueChange={setSelectedModel}>
                        <SelectTrigger className="w-full bg-black/60 border border-white/40 text-white">
                          <SelectValue placeholder="Select model">
                            {(() => {
                              const model = llmModels?.find(m => m.id === selectedModel);
                              return model ? model.name : "Select model";
                            })()}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="bg-gray-900 border border-white/40 max-h-64 overflow-y-auto">
                          {Array.isArray(llmModels) && llmModels.length > 0 && llmModels.map((model) => (
                            <SelectItem 
                              key={model.id} 
                              value={model.id}
                              className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer py-2"
                            >
                              <div className="flex justify-between items-center w-full">
                                <span className="font-medium">{model.name}</span>
                                <span className="text-xs text-gray-400">({model.provider})</span>
                              </div>
                            </SelectItem>
                          ))}
                          {(!llmModels || llmModels.length === 0) && (
                            <SelectItem 
                              value="b7cad7e6-7369-4a1a-a3e1-1923615d6ccc"
                              className="text-white hover:bg-white/10 focus:bg-white/10 py-2"
                            >
                              <div className="flex justify-between items-center w-full">
                                <span className="font-medium">GPT-4o Mini</span>
                                <span className="text-xs text-gray-400">(openai)</span>
                              </div>
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-400 mt-1">
                        GPT-4o Mini is recommended for development, GPT-4o for production
                      </p>
                    </div>

                    {/* Avatar Selection (Template) */}
                    <div>
                      <label className="block text-white mb-2">Avatar</label>
                      <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                        <SelectTrigger className="w-full bg-black/60 border border-white/40 text-white">
                          <SelectValue placeholder="Select avatar">
                            {(() => {
                              if (selectedTemplate === "00000000-0000-0000-0000-000000000000") return "No Avatar";
                              const template = templates?.find(t => t.id === selectedTemplate);
                              return template ? template.name : "Select avatar";
                            })()}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="bg-gray-900 border border-white/40 max-h-64 overflow-y-auto">
                          <SelectItem 
                            value="00000000-0000-0000-0000-000000000000"
                            className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer py-2"
                          >
                            No Avatar
                          </SelectItem>
                          {Array.isArray(templates) && templates.length > 0 && templates.map((template) => (
                            <SelectItem 
                              key={template.id} 
                              value={template.id}
                              className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer py-2"
                            >
                              {template.name}
                            </SelectItem>
                          ))}
                          {(!templates || templates.length === 0) && (
                            <SelectItem 
                              value="00000000-0000-0000-0000-000000000001"
                              className="text-white hover:bg-white/10 focus:bg-white/10 py-2"
                            >
                              General Assistant
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-400 mt-1">
                        Choose an avatar for your assistant
                      </p>
                    </div>

                    {/* Language Selection */}
                    <div>
                      <label className="block text-white mb-2">Language</label>
                      <Select value={language} onValueChange={setLanguage}>
                        <SelectTrigger className="w-full bg-black/60 border border-white/40 text-white">
                          <SelectValue placeholder="Select language" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-900 border border-white/40">
                          <SelectItem 
                            value="es"
                            className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer py-2"
                          >
                            Spanish
                          </SelectItem>
                          <SelectItem 
                            value="en"
                            className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer py-2"
                          >
                            English
                          </SelectItem>
                          <SelectItem 
                            value="pt"
                            className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer py-2"
                          >
                            Portuguese
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Memory Enabled Toggle */}
                    <div className="flex items-center justify-between">
                      <label className="text-white">Enable Memory</label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={memoryEnabled}
                          onChange={(e) => setMemoryEnabled(e.target.checked)}
                        />
                        <div className="
                          w-11 h-6 bg-gray-700 rounded-full
                          peer-checked:bg-[#6B3FC1]
                          peer-focus:ring-2 peer-focus:ring-purple-500
                          after:content-[''] after:absolute after:top-0.5 after:left-[2px]
                          after:bg-white after:border after:border-gray-300
                          after:rounded-full after:h-5 after:w-5 after:transition-all
                          peer-checked:after:translate-x-full" />
                      </label>
                    </div>

                    {/* Draft Mode Toggle */}
                    <div className="flex items-center justify-between">
                      <label className="text-white">Draft Mode</label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={isDraft}
                          onChange={(e) => setIsDraft(e.target.checked)}
                        />
                        <div className="
                          w-11 h-6 bg-gray-700 rounded-full
                          peer-checked:bg-orange-600
                          peer-focus:ring-2 peer-focus:ring-orange-500
                          after:content-[''] after:absolute after:top-0.5 after:left-[2px]
                          after:bg-white after:border after:border-gray-300
                          after:rounded-full after:h-5 after:w-5 after:transition-all
                          peer-checked:after:translate-x-full" />
                      </label>
                    </div>

                    {/* Skills */}
                    <div>
                      <label className="block text-white mb-2">Skills</label>
                      <Select value={selectedSkills} onValueChange={setSelectedSkills}>
                        <SelectTrigger className="w-full bg-black/60 border border-white/40 text-white">
                          <SelectValue placeholder="No skill selected">
                            {(() => {
                              if (selectedSkills === "none") return "No skill";
                              const skill = availableSkills?.find(s => s.name === selectedSkills);
                              return skill ? skill.friendly_name : selectedSkills;
                            })()}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="bg-gray-900 border border-white/40 max-h-64 overflow-y-auto">
                          <SelectItem 
                            value="none"
                            className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer py-2"
                          >
                            No skill
                          </SelectItem>
                          {Array.isArray(availableSkills) && availableSkills.length > 0 && availableSkills.map((skill) => (
                            <SelectItem 
                              key={skill.name} 
                              value={skill.name}
                              className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer py-2"
                            >
                              {skill.friendly_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-400 mt-1">
                        Choose a skill for your assistant (optional)
                      </p>
                    </div>

                    {/* Model Configuration */}
                    <div>
                      <p className="text-white mb-2">Model Configuration</p>
                      
                      {/* Max Tokens */}
                      <div className="mb-4">
                        <label className="text-gray-300 flex justify-between mb-1">
                          <span>Max Tokens</span>
                          <span>{maxTokens}</span>
                        </label>
                        <Slider 
                          value={maxTokens} 
                          onChange={setMaxTokens} 
                          min={256}
                          max={4096} 
                          step={256}
                          className="w-full"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Maximum length of the assistant's response
                        </p>
                      </div>

                      {/* Response format */}
                      <div className="mb-4">
                        <label className="text-gray-300 mb-1 block">Response format</label>
                        <Select value={responseFormat} onValueChange={setResponseFormat}>
                          <SelectTrigger className="w-full bg-black/60 border border-white/40 text-white">
                            <SelectValue placeholder="text" />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-900 border border-white/40">
                            <SelectItem 
                              value="text"
                              className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer py-2"
                            >
                              text
                            </SelectItem>
                            <SelectItem 
                              value="json"
                              className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer py-2"
                            >
                              json
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Output Mode */}
                      <div className="mb-4">
                        <label className="text-gray-300 mb-1 block">Output Mode</label>
                        <Select value={outputMode} onValueChange={setOutputMode}>
                          <SelectTrigger className="w-full bg-black/60 border border-white/40 text-white">
                            <SelectValue placeholder="Select output mode" />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-900 border border-white/40">
                            <SelectItem value="normal" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Normal</SelectItem>
                            <SelectItem value="structured_json" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Structured JSON</SelectItem>
                            <SelectItem value="stream" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Stream</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Conditional fields for structured_json and stream */}
                      {(outputMode === 'structured_json' || outputMode === 'stream') && (
                        <div className="grid grid-cols-2 gap-4 p-5 bg-purple-900/10 border border-purple-500/30 rounded-lg mb-4">
                          {/* Left: JSON Schema */}
                          <div>
                            <label className="text-white text-sm font-medium mb-2 block">JSON Schema</label>
                            <textarea
                              value={jsonSchema}
                              onChange={(e) => setJsonSchema(e.target.value)}
                              placeholder='{"type": "object", "properties": {...}}'
                              className="w-full bg-black/60 border border-white/20 text-white rounded-lg p-3 h-[280px] font-mono text-sm placeholder-gray-500 focus:border-purple-400 focus:ring-1 focus:ring-purple-400/50 transition-colors resize-none"
                            />
                            <p className="text-xs text-gray-400 mt-1.5">Enter a valid JSON schema for structured output</p>
                          </div>
                          
                          {/* Right: Output Options */}
                          <div>
                            <label className="text-white text-sm font-medium block mb-3">Output Options</label>
                            
                            <div className="space-y-3">
                              <div className="flex items-center justify-between p-3 bg-black/20 rounded-lg border border-white/10">
                                <label htmlFor="strictValidation" className="text-sm text-gray-300 cursor-pointer flex-1">
                                  Strict Validation
                                </label>
                                <input
                                  type="checkbox"
                                  id="strictValidation"
                                  checked={strictValidation}
                                  onChange={(e) => setStrictValidation(e.target.checked)}
                                  className="w-4 h-4 rounded cursor-pointer accent-purple-600"
                                />
                              </div>
                              
                              <div className="flex items-center justify-between p-3 bg-black/20 rounded-lg border border-white/10">
                                <label htmlFor="prettyPrint" className="text-sm text-gray-300 cursor-pointer flex-1">
                                  Pretty Print
                                </label>
                                <input
                                  type="checkbox"
                                  id="prettyPrint"
                                  checked={prettyPrint}
                                  onChange={(e) => setPrettyPrint(e.target.checked)}
                                  className="w-4 h-4 rounded cursor-pointer accent-purple-600"
                                />
                              </div>
                              
                              <div className="p-3 bg-black/20 rounded-lg border border-white/10">
                                <label className="text-sm text-gray-300 mb-2 block">Indent Spaces</label>
                                <input
                                  type="number"
                                  min="1"
                                  max="8"
                                  value={indent}
                                  onChange={(e) => setIndent(parseInt(e.target.value) || 2)}
                                  className="w-20 bg-black/60 border border-white/20 text-white rounded-lg px-3 py-2 text-sm focus:border-purple-400 focus:ring-1 focus:ring-purple-400/50 transition-colors"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Temperature */}
                      <div className="mb-4">
                        <label className="text-gray-300 flex justify-between mb-1">
                          <span>Temperature</span>
                          <span>{(temperature / 100).toFixed(2)}</span>
                        </label>
                        <Slider 
                          value={temperature} 
                          onChange={setTemperature} 
                          max={200}
                          className="w-full"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Controls randomness: 0 = focused, 2 = creative
                        </p>
                      </div>
                    </div>

                    {/* Sections Manager removed for new assistant creation */}
                  </div>
                </div>
          </div>
        </div>
      </div>
    )
  }

  // ------------------ PANTALLA "EDIT ASSISTANT" ------------------
  if (editing && editingAssistant) {
    // Sincronizar valores iniciales de modelo y template con el assistant seleccionado
    const initialModelId = editingAssistant?.config?.llm_id || selectedModel;
    const initialTemplateId = editingAssistant?.config?.assistant_template?.id || selectedTemplate;

    return (
      <AssistantConfigureView
        assistant={editingAssistant}
        onAssistantChange={setEditingAssistant}
        onBack={handleBackFromEditing}
        onSave={handleCreate}
        onUpdate={handleUpdate}
        isUpdating={submitting}
        llmModels={llmModels}
        templates={templates}
        initialModelId={initialModelId}
        initialTemplateId={initialTemplateId}
        sections={sections}
        setSections={setSections}
      />
    )
  }

  // ------------------ VISTA PRINCIPAL GRID/LIST ------------------
  return (
    <div className="flex min-h-screen opacity-0 animate-blur-reveal [animation-fill-mode:forwards]">

      <div className={`flex-1 ${showSidebar ? 'pl-4 md:pl-8 lg:pl-12 xl:pl-16' : ''} mt-8`}>
        <div className="flex items-center justify-between mb-8 opacity-0 animate-reveal-down [animation-fill-mode:forwards]" style={{ animationDelay: '0.1s' }}>
          <h2 className="text-3xl font-bold text-white">My Assistants</h2>
          <div className="flex items-center gap-4">
            {/* Botones de vista y crear nuevo */}
            <Button
              variant={viewMode === "grid" ? "default" : "outline"}
              className="flex items-center justify-center w-10 h-10 p-0 transition-all duration-300 hover:scale-105"
              onClick={() => setViewMode("grid")}
              aria-label="Grid view"
            >
              <LayoutGrid className="w-5 h-5" />
            </Button>
            <Button
              variant={viewMode === "list" ? "default" : "outline"}
              className="flex items-center justify-center w-10 h-10 p-0 transition-all duration-300 hover:scale-105"
              onClick={() => setViewMode("list")}
              aria-label="List view"
            >
              <LayoutList className="w-5 h-5" />
            </Button>
            <Button
              onClick={handleOpenCreate}
              disabled={assistantsLoading}
              className="bg-[#7c3aed] hover:bg-[#6B3FC1] disabled:bg-gray-600 text-white px-5 py-2 rounded-xl font-light text-lg flex items-center transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-purple-500/25"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create New
            </Button>
          </div>
        </div>

        {/* Chat con assistant: se muestra debajo de los botones, sobre los cards */}
        {chattingAssistant ? (
          <div 
            className="bg-[#18181b] border-white/20 text-white w-full max-w-7xl mx-auto rounded-2xl shadow-2xl flex flex-row overflow-hidden mb-8 h-[800px] opacity-0 animate-scale-in [animation-fill-mode:forwards]"
            onWheel={handleChatContainerScroll}
          >
            {/* Sidebar historial de chats SOLO en chat panel, con fade y flecha */}
<div className="w-80 min-w-[320px] max-w-[320px] bg-white/10 border-r border-white/20 flex-shrink-0 flex flex-col relative">

  <div className="flex items-center justify-between p-4 border-b border-white/10">
    <span className="text-lg font-semibold text-white">Chat Histories</span>
  </div>

  <div
    className=" flex-1 overflow-y-auto"
    id="chat-history-scroll-chat"
    style={{
      position: "relative",
      height: "calc(600px - 120px)", // Altura fija menos header
      overflowY: "auto",
    }}
    onWheel={handleMessageScroll}
  >

<div
  style={{
    position: "sticky",
    top: 0,
    height: 48,
    background: "linear-gradient(to bottom, rgba(44,44,54,0.85) 0%, rgba(44,44,54,0) 90%)",
    zIndex: 10,
    pointerEvents: "none",
  }}
/>


    {chatHistories.length === 0 ? (
      <div className="text-gray-400 ml-4 mt-4">No chat histories found.</div>
    ) : (
      <div className="space-y-1 pb-10 -mt-6">
        {chatHistories.map((history, idx) => (
          <div
            key={history.id}
            className="hover:bg-white/5 transition-all duration-150 cursor-pointer flex items-center p-3 rounded-lg group opacity-0 animate-fade-in"
            style={{ animationDelay: `${idx * 0.03}s`, animationFillMode: 'forwards' }}
            onClick={() => handleSelectHistory(history)}
          >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-white text-sm truncate">
                    {history.title || "Chat"}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 truncate">
                    {new Date(history.createdAt).toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {hasScrollableHistories && (
          <div
            style={{
              position: "sticky",
              bottom: 0,
              left: 0,
              right: 0,
              height: 40,
              pointerEvents: "none",
              marginBottom: '-4px',
            }}
          >
            <div
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: 40,
                background:
                  "linear-gradient(to top, rgba(44,44,54,0.22) 70%, rgba(44,44,54,0))",
                zIndex: 1,
                pointerEvents: "none",
              }}
            />

            <button
              type="button"
              aria-label="Scroll down"
              style={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                bottom: 12,
                zIndex: 2,
                background: "rgba(44,44,54,0.92)",
                borderRadius: "9999px",
                padding: 0,
                width: 36,
                height: 36,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "auto",
                cursor: "pointer",
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              }}
              onClick={() => {
                const el = document.getElementById("chat-history-scroll-chat");
                if (el) el.scrollBy({ top: 170, behavior: "smooth" });
              }}
            >
              <svg width="24" height="24" fill="none" stroke="white" strokeWidth="2">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
          </div>
        )}
  </div>
</div>


            {/* Main chat area */}
            <div className="flex-1 flex flex-col min-w-0">
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <img src={chattingAssistant.avatar || assistantsBot} alt={chattingAssistant.name} className="w-10 h-10 rounded-lg object-cover" />
                  <div>
                    <h3 className="text-lg font-semibold">{chattingAssistant.name}</h3>
                    <p className="text-xs text-gray-400">{chattingAssistant.description}</p>
                  </div>
                </div>
                <button onClick={handleCloseChat} className="text-gray-400 hover:text-white p-1"><X className="w-5 h-5" /></button>
              </div>
              <div className="flex-1 p-4" style={{ height: "calc(600px - 120px)", overflow: "hidden" }}>
                <div className="overflow-y-auto h-full" id="chat-message-scroll-area" style={{ height: "100%" }}>
                  {chatMessages.length === 0 ? (
                    <div className="text-center text-gray-500 mt-10">Start the conversation!</div>
                  ) : (
                    <div className="space-y-3">
                      {chatMessages.map((msg, idx) => (
                        <div key={idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                          <div
                            className={`rounded-lg px-4 py-2 max-w-[80%] ${msg.role === "user" ? "bg-purple-700 text-white message-bubble-user" : "bg-gray-800 text-gray-100 message-bubble-assistant"}`}
                            style={{
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word',
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {Array.isArray(msg.contentBlocks)
                              ? msg.contentBlocks.map((cb, i) => (
                                  <ReactMarkdown
                                    key={i}
                                    components={{
                                      code({node, ...props}) {
                                        return (
                                          <pre style={{
                                            background: '#23272e',
                                            color: '#fff',
                                            padding: '8px',
                                            borderRadius: '8px',
                                            overflowX: 'auto',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-all',
                                            maxWidth: '100%',
                                          }}>
                                            <code {...props} />
                                          </pre>
                                        );
                                      },
                                      img({node, ...props}) {
                                        return (
                                          <img
                                            {...props}
                                            alt=""
                                            className="rounded-lg max-w-full h-auto my-2"
                                            loading="lazy"
                                            crossOrigin="anonymous"
                                            referrerPolicy="no-referrer"
                                          />
                                        );
                                      }
                                    }}
                                  >{typeof cb.data?.text === 'string' ? cb.data.text : JSON.stringify(cb.data) || '[No content]'}</ReactMarkdown>
                                ))
                              : (
                                  <ReactMarkdown
                                    components={{
                                      code({node, ...props}) {
                                        return (
                                          <pre style={{
                                            background: '#23272e',
                                            color: '#fff',
                                            padding: '8px',
                                            borderRadius: '8px',
                                            overflowX: 'auto',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-all',
                                            maxWidth: '100%',
                                          }}>
                                            <code {...props} />
                                          </pre>
                                        );
                                      },
                                      img({node, ...props}) {
                                        return (
                                          <img
                                            {...props}
                                            alt=""
                                            className="rounded-lg max-w-full h-auto my-2"
                                            loading="lazy"
                                            crossOrigin="anonymous"
                                            referrerPolicy="no-referrer"
                                          />
                                        );
                                      }
                                    }}
                                  >{msg.content || '[No content]'}</ReactMarkdown>
                                )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="p-4 border-t border-white/10 bg-black/10 flex gap-2 items-center">
                <button
                  onClick={async () => {
                    if (!chattingAssistant) return;
                    setChatMessages([]);
                    setChatInput("");
                    setChatHistoryId(null);
                    // No crear chat history aquí, se creará cuando se envíe el primer mensaje
                    // Solo abrir el sidebar si no está abierto ya
                    if (!showSidebar) {
                      await openSidebar();
                    }
                  }}
                  className="bg-gray-700 hover:bg-gray-800 text-white px-3 py-2 rounded-lg mr-2 flex items-center gap-2"
                  aria-label="New Conversation"
                >
                  {/* Icono de + */}
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                  {/* Icono de chat */}
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                </button>
                <input
                  type="text"
                  className="flex-1 bg-gray-900 text-white rounded-lg px-3 py-2 border border-white/20 focus:outline-none"
                  placeholder="Type your message..."
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") handleSendMessage(); }}
                  disabled={chatSending}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={chatSending || !chatInput.trim()}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center justify-center"
                >
                  {chatSending ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        ) : (
          // Cards de assistants solo si NO hay chat abierto
          <React.Fragment>
            {/* Mostrar loading o estado vacío si no hay asistentes para el proyecto actual */}
            {assistantsLoading ? (
              /* Skeleton loading cards */
              <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(220px,220px))] justify-center">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div 
                    key={i} 
                    className="rounded-3xl overflow-hidden w-[220px] bg-white/5 border border-white/10"
                  >
                    <div className="pt-4 pb-3 flex items-center justify-center">
                      <div className="w-[140px] h-[140px] rounded-lg bg-white/10 skeleton" />
                    </div>
                    <div className="px-4 pb-4 space-y-3">
                      <div className="h-4 w-3/4 bg-white/10 rounded skeleton" />
                      <div className="h-3 w-full bg-white/10 rounded skeleton" />
                      <div className="h-3 w-2/3 bg-white/10 rounded skeleton" />
                      <div className="flex justify-between pt-2">
                        <div className="h-6 w-16 bg-white/10 rounded-full skeleton" />
                        <div className="flex gap-2">
                          <div className="h-8 w-8 bg-white/10 rounded-lg skeleton" />
                          <div className="h-8 w-8 bg-white/10 rounded-lg skeleton" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredAssistants.length === 0 ? (
              <div className="text-center py-20 opacity-0 animate-scale-reveal [animation-fill-mode:forwards]">
                <div className="text-gray-400 text-lg mb-4">No assistants found for this project</div>

              </div>
            ) : (
            <>
            {/* Cards de assistants en modo grid */}
            {viewMode === "grid" ? (
  <div className="relative">
    {/* Overlay para opacar todo cuando hay hover - siempre presente con transición suave */}
    <div
      className={`fixed inset-0 z-20 bg-black/20 pointer-events-none transition-opacity duration-500 ease-out ${
        hoveredAssistantId !== null ? 'opacity-100' : 'opacity-0'
      }`}
    />
    <div 
      className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(220px,220px))] justify-center relative z-30"
      onMouseLeave={() => setHoveredAssistantId(null)}
    >
      {filteredAssistants.map((assistant, index) => {
        const isHovered = hoveredAssistantId === assistant.id;
        return (
          <AssistantCard
            key={assistant.id}
            assistant={assistant}
            index={index}
            isHovered={isHovered}
            hoveredAssistantId={hoveredAssistantId}
            onMouseEnter={() => setHoveredAssistantId(assistant.id)}
            onMouseLeave={() => setHoveredAssistantId(null)}
            onClick={() => handleSelectAssistant(assistant.id)}
            onChatClick={handleOpenChat}
            onDelete={handleDelete}
            assistantsBot={assistantsBot}
          />
        );
      })}
    </div>
  </div>
      ) : (
        <div className="space-y-4">
          {filteredAssistants.map((assistant) => (
            <div 
              key={assistant.id} 
              onClick={() => handleSelectAssistant(assistant.id)}
              className="rounded-2xl p-4 hover:bg-white/5 transition cursor-pointer bg-white/10 backdrop-blur-sm border border-white/20"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <img 
                    src={assistant.avatar || assistantsBot} 
                    alt={assistant.name} 
                    className="w-12 h-12 object-cover rounded-lg" 
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-bold text-white">{assistant.name}</h3>
                      {assistant.isDraft && (
                        <span className="text-xs px-2 py-1 rounded-full bg-yellow-900/30 text-yellow-400">
                          Draft
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-400 mt-1">{assistant.description || 'No description'}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Model: {assistant.config?.model_label || 'Unknown'} • Language: {assistant.config?.language?.toUpperCase() || 'N/A'}
                    </p>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="text-gray-400 hover:text-white hover:bg-white/10 rounded-lg"
                      onClick={(e) => e.stopPropagation()} // Evitar propagación al GlassPanel
                    >
                      <MoreHorizontal className="w-5 h-5" />
                    </Button>
                  </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-black/90 border border-gray-800 rounded-xl shadow-lg">
                  <DropdownMenuItem 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(assistant.id);
                    }}
                    className="text-red-400 hover:bg-red-900/60 hover:text-red-300"
                  >
                    Delete
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenChat(assistant);
                    }}
                    className="text-slate-300 hover:bg-black/60 hover:text-white"
                  >
                    Chat
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </div>
            </div>
          ))}
        </div>
      )}
            </>
            )}
          </React.Fragment>
        )}
      
      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Delete Assistant"
        message="This action cannot be undone. Are you sure you want to delete this assistant?"
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
      </div>
    </div>
  );
}
